import { z } from 'zod'

import { pagedSchema } from '../core'

const text = z.string().nullish()

/**
 * The backend's background task (upload processing, generation). The stream and the plain GET send the same shape. A few
 * likely alternative names (`taskId`, `percent`) are accepted, so a slightly different frame does not stop the progress bar.
 */
export const sseTaskSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== 'object') return value
    const raw = value as Record<string, unknown>
    return { ...raw, id: raw.id ?? raw.taskId, progress: raw.progress ?? raw.percent ?? 0, stage: raw.stage ?? raw.currentStage ?? raw.step, message: raw.message ?? raw.detail }
  },
  z.object({
    id: z.string(),
    projectId: text,
    taskType: text,
    status: text,
    progress: z.number().default(0),
    /** What the server says it is doing ("Rendering"), when a frame says. */
    stage: text,
    message: text,
    /** Whatever the task produced; its shape depends on the kind of task. */
    result: z.unknown().optional(),
    errorMessage: text,
    createdAt: text,
    updatedAt: text,
  }),
)
export type SseTaskDto = z.infer<typeof sseTaskSchema>

export const sseTaskPageSchema = pagedSchema(sseTaskSchema)

export interface SseTaskFilters {
  projectId?: string
  status?: string
}

export type TaskPhase = 'queued' | 'running' | 'done' | 'failed'

/**
 * The task's state in the app's four words, whatever case or wording the backend uses ("Completed", "SUCCEEDED", "in_progress"…).
 * A status it does not know is judged by its progress: 100 or more is done, anything started is running.
 */
export function taskPhase(task: Pick<SseTaskDto, 'status' | 'progress' | 'errorMessage'> | null | undefined): TaskPhase {
  if (!task) return 'queued'
  const status = (task.status ?? '').toLowerCase().replace(/[\s_-]+/g, '')
  if (/^(failed|failure|error|errored|cancelled|canceled|aborted)$/.test(status)) return 'failed'
  if (/^(completed|complete|succeeded|success|done|finished)$/.test(status)) return 'done'
  if (/^(queued|pending|created|waiting|scheduled)$/.test(status)) return 'queued'
  if (/^(processing|running|inprogress|started|active)$/.test(status)) return 'running'
  if (task.errorMessage) return 'failed'
  return task.progress >= 100 ? 'done' : task.progress > 0 ? 'running' : 'queued'
}

/**
 * What a task makes, read from its type. The server's types: `IMAGE_GENERATION` (the first design) and `IMAGE_REGENERATION` (a new version of
 * it) make the project's design; `TEMPLATE_IMAGE_GENERATION` makes a variation of the approved template; `PROJECT_ASSETS_RECEIVED` and
 * `INSTRUCTION_ANALYSIS` process what was uploaded or written. Case and separators do not matter.
 */
const typeOf = (task: Pick<SseTaskDto, 'taskType'>) => (task.taskType ?? '').toLowerCase().replace(/[\s_-]+/g, '')

/** Whether a task is the making of a variation of an approved template ("Edit with AI" on a template, or Generate templates). */
export const isTemplateVersionTask = (task: Pick<SseTaskDto, 'taskType'>) => /^template(image)?(generation|version)$/.test(typeOf(task))

/** Whether a task is the making of the project's design (the first one or a regeneration), as opposed to a template variation or processing an upload. */
export const isGenerationTask = (task: Pick<SseTaskDto, 'taskType'>) => !isTemplateVersionTask(task) && /^(image(re)?generation|generat)/.test(typeOf(task))

/** The template variation a finished task made, when the server says (`result.templateVersionId`). A task still running does not say. */
export function templateVersionIdOf(task: Pick<SseTaskDto, 'result'>): string | null {
  const result = task.result
  const id = result && typeof result === 'object' ? (result as Record<string, unknown>).templateVersionId : null
  return typeof id === 'string' && id ? id : null
}

export const isTerminal = (phase: TaskPhase) => phase === 'done' || phase === 'failed'
